import { DeviceStatusIcon } from '../components/DeviceStatusBadge';
import {
  HardwareDeviceCategory,
  HardwareDeviceStatus,
  HardwareStatusSummary,
} from '../types/HardwareStatus';
import {
  ADC_DEVICE_STATUS_CODES,
  ADC_DEVICE_TYPE_CODES,
} from '../constants/alarm';

const isRemoved = (hardware: HardwareDeviceStatus): boolean => hardware.status === 'removed'
  || hardware.statusCodes.includes(ADC_DEVICE_STATUS_CODES.REMOVED);

const isFrontName = (name: string): boolean => name.toLowerCase().includes('front');

const findPrimaryLock = (
  hardwareStatuses: HardwareDeviceStatus[],
): HardwareDeviceStatus | undefined => {
  const activeLocks = hardwareStatuses.filter(
    hardware => hardware.category === 'door_lock' && !isRemoved(hardware),
  );
  return activeLocks.find(hardware => isFrontName(hardware.deviceName)) || activeLocks[0];
};

export const getPrimaryAlarmHardwareStatuses = (
  hardwareStatuses: HardwareDeviceStatus[],
): HardwareDeviceStatus[] => {
  const doorLock = findPrimaryLock(hardwareStatuses);
  const panels = hardwareStatuses.filter(
    hardware => hardware.deviceType === ADC_DEVICE_TYPE_CODES.PARTITION,
  );
  const panel = panels.find(hardware => !isRemoved(hardware)) || panels[0];

  return [doorLock, panel].filter(
    (hardware): hardware is HardwareDeviceStatus => Boolean(hardware),
  );
};

export const getDeviceStatusIcon = (status?: HardwareStatusSummary): DeviceStatusIcon => {
  if (!status || status.status === 'unknown') {
    return 'unknown';
  }
  return 'system';
};

export const getLockStatusIcon = (status?: HardwareStatusSummary): DeviceStatusIcon => {
  if (!status || status.status === 'unknown' || status.status === 'not_available') {
    return 'unknown';
  }
  if (status.status === 'unlocked' || status.status === 'open' || status.status === 'opening') {
    return 'unlock';
  }
  return 'lock';
};

const HARDWARE_STATUS_ICONS: {
  [category in Exclude<HardwareDeviceCategory, 'door_lock' | 'other'>]: DeviceStatusIcon;
} = {
  system: 'system',
  garage_door: 'garage',
  thermostat: 'thermostat',
  contact_sensor: 'contact',
  motion_sensor: 'motion',
  smoke_sensor: 'smoke',
  environment_sensor: 'environment',
  water_sensor: 'water',
  camera: 'camera',
  light: 'light',
  glass_break_sensor: 'glass_break',
  panic_button: 'panic',
  control: 'control',
  siren: 'siren',
  doorbell: 'doorbell',
  repeater: 'repeater',
  gateway: 'gateway',
  power: 'power',
  location: 'location',
  medical: 'medical',
  audio: 'audio',
  safe: 'safe',
  access: 'access',
  module: 'module',
};

export const getHardwareStatusIcon = (
  hardware: HardwareDeviceStatus,
): DeviceStatusIcon => {
  if (hardware.category === 'door_lock') {
    return getLockStatusIcon(hardware);
  }
  if (hardware.category === 'other') {
    return hardware.status === 'unknown' || hardware.status.startsWith('unknown_')
      ? 'unknown'
      : 'device';
  }
  return HARDWARE_STATUS_ICONS[hardware.category];
};
